import { z } from "zod";

/**
 * The long-running operation's two bodies (`04` §4.5.10) and the audit export's poll, **one contract for the door and
 * for every client** (`GENERIC-DOOR-SWEEP`, Arc 1, A48).
 *
 * `POST /api/v1/audit/exports` answered `Promise<unknown>` and so did its poll; the shape lived in a worker's
 * `succeed(...)` call and in a spec's local cast. {@link operationAcceptedSchema} is the `202` body (any operation's),
 * {@link auditExportPollSchema} is the poll of THIS kind: `result` is the worker's record of what it produced and
 * `download` is signed at the poll, never stored. Strict on purpose, as `notifications-answer.ts` is.
 */
export const operationAcceptedSchema = z.strictObject({
  operationId: z.string(),
  status: z.literal("pending"),
  /** Where to poll, relative to the API's origin; also the `Location` header. */
  pollUrl: z.string(),
});

/** What the export worker records when it succeeds (`audit/infra/export.ts`). */
export const auditExportResultSchema = z.strictObject({
  rowCount: z.number().int().min(0),
  sha256: z.string(),
  firstRowHash: z.string().nullable(),
  lastRowHash: z.string().nullable(),
  regulatoryRecordId: z.string(),
  documentId: z.string(),
  fileName: z.string(),
});

/** What the worker stores on failure: `{ error: { code, message } }`, the sentence the requester is shown. */
export const operationFailureSchema = z.strictObject({
  error: z.strictObject({ code: z.string(), message: z.string() }),
});

export const auditExportPollSchema = z.strictObject({
  status: z.enum(["pending", "running", "succeeded", "failed"]),
  /** 0 to 100. */
  progress: z.number().int().min(0).max(100),
  result: auditExportResultSchema.optional(),
  error: operationFailureSchema.optional(),
  /** Only on `succeeded`: the file's URL, signed NOW for fifteen minutes. */
  download: z.strictObject({ url: z.string(), expiresInSeconds: z.number().int().positive() }).optional(),
});

export type OperationAccepted = z.infer<typeof operationAcceptedSchema>;
export type AuditExportPoll = z.infer<typeof auditExportPollSchema>;
